import formBasicMixin from '@phoenix/mixins/formBasicMixin';
import formAddressFieldsMixin from '@phoenix/mixins/formAddressFieldsMixin';
import { mapActions } from 'vuex';
import Cookies from 'js-cookie';
import { getPasswordRequirements } from '@phoenix/helpers/form-helper';
import { FEATURE_FLAG, getFeatureFlag } from '@phoenix/helpers/feature-flag-helper';
import { zipObject, map, get, extend } from 'lodash-es';
import { showModal } from '@phoenix/helpers/modal-helper';
import { emitter, USER_REGISTERED } from '@phoenix/event-bus';
import moment from '@phoenix/moment';

export default {
  mixins: [formBasicMixin, formAddressFieldsMixin],

  props: {
    countryCode: {
      type: String,
      required: true,
    },
    redirectUrl: {
      type: String,
      default: '',
    },
    terms: {
      type: String,
      default: '',
    },
    shouldDisplaySubmitButton: {
      type: Boolean,
      default: true,
    },
    showErrorsInToast: {
      type: Boolean,
      default: true,
    },
    customOnTokenFail: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      formConfigName: 'signUp',
      values: {},
      fields: {
        newsletter: {
          id: 'newsletter',
          name: 'newsletter',
          label: this.$t('Form.stayInTouch', {
            privacyLink: `<a target='_blank' class='rcms_privacypol link link--text' href='${this.terms}'>${this.$t(
              'Form.privacy',
            )}</a>`,
          }),
          value: false,
          type: 'checkbox',
        },
        password: {
          id: 'password',
          name: 'password',
          type: 'password',
          label: this.$t('Form.password'),
          required: true,
          value: null,
          requirements: getPasswordRequirements(),
        },
        passwordConfirmation: {
          id: 'password_confirmation',
          name: 'password_confirmation',
          type: 'password',
          label: this.$t('Form.password_confirmation'),
          required: true,
          value: null,
        },
        country: {
          id: 'country',
          name: 'country',
          label: this.$t('Form.label.country'),
          value: null,
          options: [],
          required: true,
        },
      },
    };
  },

  computed: {
    formActionUrl() {
      return window.route('api.customer.registration');
    },

    /**
     * TODO: Clean me
     * Allow us to have an object with key value like { firstName: abc, mobilePhone: 124 }
     * @returns object
     */
    formattedFormData() {
      return extend(
        zipObject(
          map(this.fields, (field) => field.name),
          map(this.fields, (field) => field.value),
        ),
        this.$refs.phoenixForm.getChinesePiplFormData(),
      );
    },

    phoneHelpText() {
      if (this.isChineseInPDG && this.privacyLink && getFeatureFlag(FEATURE_FLAG.DISPLAY_DISCLAIMER_PIN_CODE)) {
        return this.$t('Form.signup.phoneHelp', { privacyLink: this.privacyLink });
      }
      return null;
    },
  },

  watch: {
    'fields.birthDate': {
      handler() {
        this.applyBirthDateMaxAge();
      },
      immediate: true,
    },
  },

  created() {
    if (getFeatureFlag(FEATURE_FLAG.GEMINI_CN)) {
      this.fields.password.required = false;
      this.fields.passwordConfirmation.required = false;
    }
  },

  mounted() {
    const currentCountryFromCookie = Cookies.get('currentCountry');
    this.fields.country.value = currentCountryFromCookie ? currentCountryFromCookie : this.countryCode;
  },

  methods: {
    ...mapActions('currentUser', [
      'setCustomerInfoFormData',
      'setCustomerInfo',
      'loadCustomerInfo',
      'createCustomerWithToken',
      'setOnSubmitToken',
    ]),

    onFail(error) {
      if (get(error, ['response', 'data', 'data', 'type']) === 'PIN') {
        this.setCustomerInfoFormData(this.formattedFormData);
        if (this.customOnTokenFail) {
          this.$emit('token-fail');
        } else {
          this.setOnSubmitToken(async (data) => {
            await this.createCustomerWithToken({
              data,
              redirectUrl: this.redirectUrl,
            });
          });
          showModal('phone-token-form-modal');
        }
      }
      this.$emit('on-fail', error);
    },

    applyBirthDateMaxAge() {
      if (this.fields.birthDate) {
        this.fields.birthDate.maxDate = moment().subtract(14, 'years').format('YYYY-MM-DD');
      }
    },

    async addTrackingInformation() {
      let email = '';
      if (this.fields?.email?.value) {
        email = this.fields.email.value;
      } else if (this.fields?.emailOrPhone?.value) {
        email = this.fields.emailOrPhone.value;
      }
      const event = await window.tracking.getAccountCreationFormTracking(email);
      window.tracking.pushEvent(event);

      emitter.emit(USER_REGISTERED);
    },
  },
};
